<template>
  <div class="tfc-wrapper">
    <div
      v-for="trueFitProductId in trueFitProductIds"
      :key="trueFitProductId"
      class="tfc-fitrec-product"
      data-theme="newsite"
      :data-styleid="trueFitProductId"
      :class="{ hide: selectedTrueFitProductId !== trueFitProductId || showSkeleton }"
      :data-userid="userId"
    />
    <Skeleton
      v-if="showSkeleton"
      :height="skeletonHeight"
      class="margin-top-xs"
    />
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import Skeleton from '@atomic-ui/skeleton';
import { Cookie } from '@common-vue/util/src/modules/cookie';
import { cookies } from 'config';
import { usePageData } from '@composables/usePageData';

import { useTrueFit } from '@composables/useTrueFit';
import { useLoadedTrueFitProduct } from '@composables/useLoadedTrueFitProduct';
import { useBrand } from '@composables/useBrand';
import { tagFitrecRegisterOpen, tagRecomendSize, tagFitrecRegisterComplete } from '../features/analytics/trueFit';

const { isBcom } = useBrand();
const showSkeleton = ref(isBcom);
const skeletonHeight = isBcom ? '26px' : '25px'; // To avoid CLS
const { productId } = usePageData();
const userId = Cookie.get(cookies.userGuid);

const emit = defineEmits(['update:touched', 'update:selectedSizeId']);
const props = defineProps({
  selectedSizeId: {
    /** @type import('vue').PropType<number | null> */
    type: Number,
    default: null,
  },
  touched: {
    type: Boolean,
    default: false,
  },
  sizeChips: {
    /** @type import('vue').PropType<Array<{id: number, text: string, name: string, unavailable: boolean}>> */
    type: Array,
    default: () => [],
  },
});

const { initTrueFit, lastSelectedSize } = useTrueFit();
const { selectedTrueFitProductId, trueFitProductIds } = useLoadedTrueFitProduct(productId);

const fitrecProductSuccessCb = (/** @type {TrueFitResponce} */ response) => {
  if (!response || props.touched) return;

  const recommendedSize = (response.fitRecommendation.size || '').toLowerCase();

  const isRecommendedSizeData = props.sizeChips.find(
    (size) => (size.text.toLowerCase() === recommendedSize || size.name.toLowerCase() === recommendedSize),
  );
  const isRecommendedSizeAvailable = !!isRecommendedSizeData && !isRecommendedSizeData.unavailable;

  if (isRecommendedSizeAvailable) {
    lastSelectedSize.value = isRecommendedSizeData.id;
    emit('update:selectedSizeId', isRecommendedSizeData.id);
  }

  tagRecomendSize(response, productId, isRecommendedSizeAvailable);
};

const fitrecRegisterOpenCb = () => {
  emit('update:touched', false);
  tagFitrecRegisterOpen({ productId });
};

onMounted(async () => {
  try {
    await initTrueFit({
      fitrecProductSuccessCb,
      fitrecRegisterOpenCb,
      fitrecRegisterCompleteCb: tagFitrecRegisterComplete,
      fitrecHideSkeletonCb: () => {
        showSkeleton.value = false;
      },
    });
  } catch (e) {
    showSkeleton.value = false;
    console.error(e); // eslint-disable-line no-console
  }
});
</script>

<style lang="scss">
@import "~settingSCSS";

.tfc-wrapper {
  display: flex;
  align-items: center;
}

.tfc-cfg-widget.tfc-fitrec-result input:focus,
.tfc-cfg-widget.tfc-fitrec-result select:focus,
.tfc-cfg-widget.tfc-fitrec-result button:focus,
.tfc-cfg-widget.tfc-fitrec-result a:focus {
  box-shadow: none!important;
  -webkit-box-shadow: none!important;
  outline: auto!important;
  outline-color: #0065ff!important;
}

.tfc-fitrec-product > div.tfc-fitrec-result.tfc-cfg-widget {
  margin: rem-calc(16 0 0 0);
}
</style>

<style lang="scss" brand="mcom">
/* rewrite default trueFit styles to avoid CLS */
.tfc-cfg-widget.tfc-fitrec-result .tfc-if-shopping-for-self,
.tfc-cfg-widget.tfc-fitrec-result .tfc-if-shopping-for-other,
.tfc-cfg-widget.tfc-fitrec-result .tfc-cfg-link,
.tfc-cfg-widget.tfc-fitrec-result .tfc-size.tfc-cfg-size{
  font-weight: 400!important;
}
</style>

<style lang="scss" brand="bcom">
.tfc-wrapper {
  min-height: 26px;
}

/* rewrite default trueFit styles to avoid CLS */
.tfc-fitrec-product .tfc-cfg-widget.tfc-fitrec-result .tfc-cfg-message {
  font-family: Avenir, Helvetica Neue, Helvetica, Arial, sans-serif!important;
  line-height: 20px!important;
  letter-spacing: normal!important;
}
.tfc-fitrec-product .tfc-cfg-widget.tfc-fitrec-result .tfc-cfg-link,
.tfc-fitrec-product .tfc-cfg-widget.tfc-fitrec-result .tfc-cfg-clickable-area,
.tfc-fitrec-product .tfc-cfg-widget.tfc-fitrec-result .tfc-cfg-size-wrapper,
.tfc-fitrec-product .tfc-cfg-widget.tfc-fitrec-result .tfc-cfg-size,
.tfc-fitrec-product .tfc-cfg-widget.tfc-fitrec-result .tfc-cfg-score-description,
.tfc-fitrec-product .tfc-cfg-widget.tfc-fitrec-result .tfc-cfg-basic-button,
.tfc-fitrec-product .tfc-cfg-widget.tfc-fitrec-result .tfc-cfg-size-label {
  font-family: Avenir, Helvetica Neue, Helvetica, Arial, sans-serif!important;
  line-height: 20px!important;
  letter-spacing: normal!important;
}
.tfc-fitrec-product .tfc-cfg-widget.tfc-fitrec-result .tfc-cfg-message-layout .tfc-cfg-logo {
  margin-top: -7px!important;
}

.tfc-fitrec-product .tfc-cfg-widget.tfc-fitrec-result .line-break-custom {
  font-size: 14px!important;
}
</style>
